import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useAxios from "../../hook/useAxios";
import { MapMarker } from "../../type/mapMarker";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useSelector } from "react-redux";
import { profileInfo } from "../../store";

const MapSection: React.FC<{
  markerList: MapMarker[];
  kmlData: string | undefined;
  search: boolean;
  searchingStateHandler: () => void;
}> = (props) => {
  const [infoMarker, setInfoMarker] = useState<{
    marker: google.maps.Marker;
    markerObj: MapMarker;
  }>();
  const profile = useSelector(profileInfo);
  const googleMapRef = useRef<HTMLDivElement>(null);
  const markerClustererRef = useRef<MarkerClusterer>();

  const { t, i18n } = useTranslation();
  let googleMap: google.maps.Map;
  let parser;
  let infoWindow: google.maps.InfoWindow = new window.google.maps.InfoWindow(
    {},
  );
  const [currentInfoWindow, setCurrentInfoWindow] =
    useState<google.maps.InfoWindow>(infoWindow);

  const { fetchData, response } = useAxios("", "GET", false, "", true, true);

  const getTranslatedValue = (field: any, lang: string) => {
    if (!field) return "";

    if (lang === "fr") {
      return field.fr === "" ? field.en : field.fr;
    } else {
      return field.en;
    }
  };

  const species = getTranslatedValue(
    response?.data?.species_name_transes,
    i18n.language,
  );
  const nursery = getTranslatedValue(
    response?.data?.nursery_name_transes,
    i18n.language,
  );
  const description = getTranslatedValue(
    response?.data?.description_transes,
    i18n.language,
  );
  const responsibleCategory =
    i18n.language === "fr"
      ? response?.data?.responsible_category_transes.fr
      : response?.data?.responsible_category_transes.en;

  const getAge = (years: number, months: number) => {
    if (years < 0 || months < 0) {
      return `<span class='age-btn'>
                    <img style='width:18px; height:18px' src='/img/info.svg' alt='negative age'/>
                    <div class='age-text'>
                      <p>${
                        i18n.language === "en"
                          ? "The age of the tree is wrong. Please check the planting date."
                          : "L'âge de l'arbre est erroné. Veuillez vérifier la date de plantation."
                      }</p>
                    </div>
                  </span>`;
    }
    if (years === 0 && months === 0) {
      return "-";
    }
    if (years > 0 && months > 0) {
      return `${years} ${t("Register.Year")}, ${months} ${t("Register.Month")}`;
    }
    if (years > 0) {
      return `${years} ${t("Register.Year")}`;
    }
    if (months > 0) {
      return `${months} ${t("Register.Month")}`;
    }
    return "";
  };

  const age = getAge(response?.data?.age_year, response?.data?.age_month);

  useEffect(() => {
    if (response) {
      props.searchingStateHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (
      response &&
      infoMarker?.markerObj.slug === response?.data?.slug &&
      !props.search
    ) {
      const url = window.location.href;
      currentInfoWindow.setContent(`
           <style>
             td {
               font-size: 14px;
              font-weight: 400;
               width : 200px;
              padding-top : 10px;
             }
             td, th {
              text-align: left;
              color:#414141;
             }
             h1{
              font-weight: 600;
              display:inline;
              color:#414141;
             }

            a{
              display:inline;
              margin-left:10px;
              color:#414141;
            }
            .age-btn {
              width:14px;
              height:14px;
              border-radius: 0.8rem;
              color: #d2202a;
              opacity: 1;
              position: relative;
              cursor: pointer;
            }
            .age-text {
              position: absolute;
              display: none;
              width: 250px;
              height:fit-content;
              left:-85px;
              top:-85px;
              padding: 10px;
              border-radius: 0.3rem;
              z-index: 10;
              background-color: white;
              color: #414141;
              overflow-wrap: break-word;
            }

            .age-btn:hover .age-text {
              display: inline;
            }
             </style><div>
             <h1>${response.data.tag}</h1>
             ${
               profile.role !== "V" &&
               `<a href='${url.split("/")[0]}/registers/edit-register/${
                 response.data.slug
               }'>`
             }<img style='width:12px; height:12px; display:inline;' src='/img/Edit pen.svg' alt='edit'></a>
        ${
          response.data.planted_tree_image
            ? `<img style='width:495px; margin-top:8px; height:300px; object-fit: cover;' src=${response.data.planted_tree_image} alt='Tree'>`
            : ""
        } <div
             style="padding: 10px; border-radius: 8px; background-color: #f7f7f7; margin-top: 10px;"
           >
           <table>
          <tr>
           <td>${t("Map.Species")}</td>
           <td>${species}</td>
         </tr>
         <tr>
         <td>
         ${t("Map.Nursery")}
         </td>
         <td >${nursery}</td>
         </tr>
         <tr>
         <td>
         ${t("Map.Height “cm”")}
         </td>
         <td >${response.data.height || "-"}</td>
         </tr>
         <tr>
         <td>
         ${t("Map.Diameter “mm”")}
         </td>
         <td >${response.data.diameter || "-"}</td>
         </tr>
         <tr>
         <td>
         ${t("Register.Age")}
         </td>
         <td > ${age}
         </td>
         </tr>
         <tr>
          <td>${t("Plantings.Date of planting")}</td>
          <td>${response.data.planting_date}</td>
         </tr>
       </table></div>
           <div
             style="
               padding: 10px;
               border-radius: 8px;
               background-color: #f7f7f7;
               margin-top: 10px;
             "
          >
           <table>

           <tr>
          <td>${t("Register.Responsible category")}</td>
          <td>${responsibleCategory}</td>
         </tr>
         <tr>
          <td>${t("Register.Responsible type")}</td>
          <td>${response.data.responsible_type || ""}</td>
         </tr>
         <tr>
          <td>${t("Register.Responsible")}</td>
          <td>${response.data.responsible_name}</td>
         </tr>
       </table>
           </div>
           <div
             style="
               padding: 10px;
               border-radius: 8px;
               background-color: #f7f7f7;
               margin-top: 10px;
            "
           >
           <table>
           <tr>
             <td>${t("Register.Latitude")}</td>
            <td>${response.data.latitude}</td>
           </tr>
          <tr>
             <td>${t("Register.Longitude")}</td>
             <td>${response.data.longitude}</td>
           </tr>
           <tr>
          <td>
           ${t("Plantings.Province")}
           </td>
           <td >${response.data.department}</td>
          </tr>
           <tr>
           <td>
           ${t("Plantings.District")}
           </td>
           <td >${response.data.municipality}</td>
           </tr>
           <tr>
           <td>
           ${t("Plantings.Community")}
           </td>
           <td >${response.data.community || ""}</td>
           </tr>
         </table>
            </div>
             <div style="padding:10px;
               border-radius: 8px;
               background-color: #f7f7f7;
               margin-top: 10px;">
               <table>
               <tr>
                 <td>${t("Map.Description")}</td>
                 <td>${description}</td>
               </tr>
             </table>
               </div>
           </div>
           
           `);
      currentInfoWindow.open(googleMap, infoMarker?.marker);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInfoWindow, response, infoMarker, props.search]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    googleMap = initGoogleMap();
    let bounds = new window.google.maps.LatLngBounds();
    const markers: google.maps.Marker[] = [];
    props.markerList.forEach((x) => {
      const marker: any = createMarker(x);
      bounds.extend(marker.position);
      markers.push(marker);
      googleMap.setCenter(new google.maps.LatLng(-14.6811018, 27.8295816));
      googleMap.setZoom(7);
    });
    markerClustererRef.current = new MarkerClusterer({
      map: googleMap,
      markers: markers,
    });

    if (props.kmlData !== "") {
      /* eslint-disable new-cap */
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line react-hooks/exhaustive-deps
      parser = new geoXML3.parser({ map: googleMap });
      /* eslint-enable */
      parser.parseKmlString(props.kmlData);
    }
  }, []);

  // initialize the google map
  const initGoogleMap = () => {
    return new window.google.maps.Map(googleMapRef.current! as HTMLElement, {
      zoom: 6,
      center: { lat: -14.6811018, lng: 27.8295816 },
      mapId: "de709e4ae98647a3",
    });
  };

  // create marker on google map
  const createMarker = (markerObj: MapMarker) => {
    const marker = new window.google.maps.Marker({
      position: { lat: markerObj.latitude, lng: markerObj.longitude },
      map: googleMap,
      icon: {
        url: markerObj.featured_icon,
        // set marker width and height
        scaledSize: new window.google.maps.Size(25, 25),
      },
    });

    marker.addListener("click", () => {
      if (currentInfoWindow) {
        currentInfoWindow.close();
      }

      fetchData(
        { slug: markerObj.slug },
        process.env.REACT_APP_API_URL +
          `/api/admin/map/${markerObj.slug}/details/`,
      );
      setInfoMarker({ marker: marker, markerObj: markerObj });
      setCurrentInfoWindow(infoWindow);
    });

    return marker;
  };
  return <div ref={googleMapRef} className="w-full h-[580px]" />;
};

export default MapSection;
